import { IRM_CODES, type IrmCode, type MeetOutcome } from "../competition/vocab";
import { RESULT_DISPLAY_MAX, RESULT_FAMILY_ARRAY_MAX, RESULT_RANK_MAX, type RoundsResultDetail, type SaveResultsBody } from "../competition/results";
import type { TsrSaveSlot } from "./sets-mapping";
import { type TsrArcheryArrow, type TsrArcheryState, type TsrShootingFeedEnvelope, type TsrShootingRow, tsrArrowPoints } from "./target";

/**
 * Save Meet for the TSR target sports — the live state → the ROUNDS family
 * body of the EXISTING `PATCH /api/meets/:id/results`. Pure, like
 * ./sets-mapping.ts: the TSR client calls it and sends the body itself as the
 * signed-in operator, so the route's group / sport / permission check and the
 * slot versions apply exactly as for an OVR save. The API has no other path
 * that persists a live score.
 *
 * ── Archery (one head-to-head of the archery controller) ───────────────────
 *   detail.rounds    one per regulation set / end, banked first, then the end
 *                    in progress when it holds arrows: `{ seq, value: the
 *                    end's arrow total, parts: [{ seq, value: arrow points }] }`
 *                    (X counts 10, M 0 — the X itself is in `tallies.x_count`)
 *   detail.tallies   `set_points` (Recurve set play), `tens` (10 + X),
 *                    `x_count`, `shoot_off` (the decider's arrow total, only
 *                    when one was shot). Tallies this mapper does not own are kept.
 *   result_value     set points (set play: "6–5" is 6 and 5), else the
 *                    regulation arrow total (cumulative: Compound, the
 *                    ranking round). The shoot-off never adds to a total —
 *                    it decides the outcome (World Archery Book 3, 14.3).
 *   result_display   the value; "147 (S-O 10)" when a shoot-off was shot
 *   outcome / rank   head-to-head only (exactly two filled slots): WIN / LOSS,
 *                    1 / 2 once the controller says finished and the score
 *                    decides it; null while live or level (closest-to-centre
 *                    is a judge's call the score sheet cannot make). A field
 *                    of more than two (a ranking round line) leaves both alone.
 *
 * ── Shooting (the range software's rows, as the file watcher sent them) ────
 *   detail.rounds    `series_1..n` (else `round_1..n`, the 25 m events), as numbers
 *   detail.tallies   `inner_tens`, `shots_fired`
 *   result_value     `final_score`, else `qualification_total`, else the row's
 *                    printed result, else the sum of the series
 *   result_display   that number as printed; "585-26x" with inner tens
 *   rank             the range software's rank (it ranks the whole field) —
 *                    with `detail.rank_locked`, so an OVR edit never re-ranks it
 *   irm              an IRM the ODF names that is one of ours (DNS, DNF, DSQ…)
 * A row names its slot by `meetParticipantId` (= `meet_competitor_id`); a row
 * for a slot that is not a filled slot of this meet is never applied.
 *
 * IRMs are otherwise not touched (absent = left alone): a walk-over is OVR's
 * call. `detail` is a whole-value replace on the server, so the slot's
 * CURRENT detail is the base and `rounds` is always replaced whole.
 */

const DASH = "–"; // en dash, as OVR prints a score

export type TsrRoundsWarning =
	/** The end in progress was saved as a round. */
	| { code: "LIVE_END_INCLUDED"; seq: number }
	/** Finished but level (or not finished): outcome / rank are sent as null. */
	| { code: "UNDECIDED" }
	/** More than two filled slots: outcome / rank are left alone. */
	| { code: "NOT_HEAD_TO_HEAD" }
	/** A shooting row whose IRM is not one of ours — the IRM was not sent. */
	| { code: "UNKNOWN_IRM"; meet_competitor_id: string; irm: string }
	/** A shooting row for a slot that is not a filled slot of this meet. */
	| { code: "FOREIGN_SLOT"; meet_competitor_id: string };

export interface TsrRoundsSaveResult {
	body: SaveResultsBody;
	warnings: TsrRoundsWarning[];
	/** Archery "6–5" / "147–147 (S-O 10–9)"; shooting the number of rows. For the save bar. */
	summary: string;
}

export class TsrRoundsMappingError extends Error {
	constructor(
		readonly code: "SLOTS_REQUIRED" | "NOTHING_TO_SAVE",
		message: string,
	) {
		super(message);
		this.name = "TsrRoundsMappingError";
	}
}

type Tally = NonNullable<RoundsResultDetail["tallies"]>[number];
type Round = NonNullable<RoundsResultDetail["rounds"]>[number];

const OWN_ARCHERY_TALLIES = new Set(["set_points", "tens", "x_count", "shoot_off"]);
const OWN_SHOOTING_TALLIES = new Set(["inner_tens", "shots_fired"]);

/** The slot's current tallies minus the ones this mapper owns, then ours. */
function mergeTallies(current: unknown, own: ReadonlySet<string>, next: Tally[]): Tally[] {
	const kept = (Array.isArray((current as RoundsResultDetail | null)?.tallies) ? (current as RoundsResultDetail).tallies! : []).filter(
		(t) => t && typeof t.code === "string" && !own.has(t.code),
	);
	return [...kept, ...next].slice(0, RESULT_FAMILY_ARRAY_MAX);
}

const baseDetail = (slot: TsrSaveSlot): RoundsResultDetail => {
	const d = slot.detail;
	return d && typeof d === "object" && !Array.isArray(d) ? ({ ...d } as RoundsResultDetail) : {};
};

const clip = (s: string): string => (s.length > RESULT_DISPLAY_MAX ? s.slice(0, RESULT_DISPLAY_MAX) : s);

// ─── Archery ─────────────────────────────────────────────────────────────────

export interface TsrArcherySaveInput {
	/** The controller's state at Save Meet. */
	state: TsrArcheryState;
	/** `[side A, side B]` — A is the first filled slot (`tsrOrderSlots` / the board's order). */
	slots: readonly [TsrSaveSlot, TsrSaveSlot];
	/** True when these are the meet's ONLY two filled slots: outcome and rank are then the controller's. */
	headToHead: boolean;
}

const sumArrows = (arrows: readonly TsrArcheryArrow[] | undefined): number => (arrows ?? []).reduce((t, a) => t + tsrArrowPoints(a?.value ?? ""), 0);

/** One end's arrows as ROUNDS parts (≤ 36 — an end is at most 6 arrows; a hand-edited state is clipped). */
const parts = (arrows: readonly TsrArcheryArrow[] | undefined) => (arrows ?? []).slice(0, 36).map((a, i) => ({ seq: i + 1, value: tsrArrowPoints(a?.value ?? "") }));

interface ArcheryEnds {
	/** Regulation ends, banked then live: per side the arrows and the total. */
	ends: { a: TsrArcheryArrow[]; b: TsrArcheryArrow[] }[];
	liveSeq: number | null;
	/** The decider's arrows, banked or in progress. Null: no shoot-off was shot. */
	shootOff: { a: TsrArcheryArrow[]; b: TsrArcheryArrow[] } | null;
}

function archeryEnds(state: TsrArcheryState): ArcheryEnds {
	const ends: ArcheryEnds["ends"] = [];
	let shootOff: ArcheryEnds["shootOff"] = null;
	for (const s of state.completedSets ?? []) {
		if (!s) continue;
		if (s.shootOff) shootOff = { a: s.arrowsA ?? [], b: s.arrowsB ?? [] };
		else ends.push({ a: s.arrowsA ?? [], b: s.arrowsB ?? [] });
	}
	const liveA = state.arrowsA ?? [];
	const liveB = state.arrowsB ?? [];
	let liveSeq: number | null = null;
	if (liveA.length > 0 || liveB.length > 0) {
		if (state.shootOff) shootOff = { a: liveA, b: liveB };
		else {
			ends.push({ a: liveA, b: liveB });
			liveSeq = ends.length;
		}
	}
	return { ends, liveSeq, shootOff };
}

/**
 * Banked sets keep the totals the controller banked (an `EDIT_SET` correction
 * clears a set's arrows and leaves only its totals), so a set's value is its
 * stored `a` / `b`, not the sum of whatever arrows survived.
 */
function regulationTotals(state: TsrArcheryState): { a: number[]; b: number[] } {
	const a: number[] = [];
	const b: number[] = [];
	for (const s of state.completedSets ?? []) {
		if (!s || s.shootOff) continue;
		a.push(Number.isFinite(s.a) ? s.a : sumArrows(s.arrowsA));
		b.push(Number.isFinite(s.b) ? s.b : sumArrows(s.arrowsB));
	}
	if (!state.shootOff && ((state.arrowsA ?? []).length > 0 || (state.arrowsB ?? []).length > 0)) {
		a.push(sumArrows(state.arrowsA));
		b.push(sumArrows(state.arrowsB));
	}
	return { a, b };
}

const count = (arrows: readonly TsrArcheryArrow[], pred: (v: string) => boolean) => arrows.filter((x) => pred(x?.value ?? "")).length;

export function tsrArcherySaveBody(input: TsrArcherySaveInput): TsrRoundsSaveResult {
	const { state, headToHead } = input;
	if (!input.slots?.[0] || !input.slots?.[1]) throw new TsrRoundsMappingError("SLOTS_REQUIRED", "Both slots (A, B) are required");
	const { ends, liveSeq, shootOff } = archeryEnds(state);
	if (ends.length === 0 && !shootOff) throw new TsrRoundsMappingError("NOTHING_TO_SAVE", "No arrow has been scored yet");

	const setPlay = state.format?.scoring === "set-points";
	const totals = regulationTotals(state);
	const warnings: TsrRoundsWarning[] = [];
	if (liveSeq !== null) warnings.push({ code: "LIVE_END_INCLUDED", seq: liveSeq });

	const regulation = (i: 0 | 1) => (i === 0 ? totals.a : totals.b).reduce((t, v) => t + v, 0);
	const setPoints = (i: 0 | 1) => (i === 0 ? state.setPointsA : state.setPointsB) ?? 0;
	const soTotal = (i: 0 | 1) => (shootOff ? sumArrows(i === 0 ? shootOff.a : shootOff.b) : null);
	const value = (i: 0 | 1) => (setPlay ? setPoints(i) : regulation(i));

	// The verdict: set points (set play — the decider's point is already in them),
	// else the regulation total, then the shoot-off arrows. Level = undecided.
	let outcomes: [MeetOutcome, MeetOutcome] | null = null;
	if (state.matchFinished) {
		const cmp = (x: number, y: number): [MeetOutcome, MeetOutcome] | null => (x > y ? ["WIN", "LOSS"] : y > x ? ["LOSS", "WIN"] : null);
		outcomes = cmp(value(0), value(1));
		if (!outcomes && !setPlay && shootOff) outcomes = cmp(soTotal(0) ?? 0, soTotal(1) ?? 0);
	}
	if (!headToHead) warnings.push({ code: "NOT_HEAD_TO_HEAD" });
	else if (!outcomes) warnings.push({ code: "UNDECIDED" });

	const results = ([0, 1] as const).map((i) => {
		const slot = input.slots[i];
		const mine = ends.map((e) => (i === 0 ? e.a : e.b));
		const all = mine.flat();
		const rounds: Round[] = (i === 0 ? totals.a : totals.b).map((v, n) => ({ seq: n + 1, value: v, parts: parts(mine[n]) })).slice(0, RESULT_FAMILY_ARRAY_MAX);
		for (const r of rounds) if (r.parts && r.parts.length === 0) delete r.parts;
		const so = soTotal(i);
		const tallies: Tally[] = [
			...(setPlay ? [{ code: "set_points", value: setPoints(i) }] : []),
			{ code: "tens", value: count(all, (v) => v === "X" || v === "10") },
			{ code: "x_count", value: count(all, (v) => v === "X") },
			...(so !== null ? [{ code: "shoot_off", value: so }] : []),
		];
		const current = baseDetail(slot);
		const detail: RoundsResultDetail = { ...current, rounds, tallies: mergeTallies(current, OWN_ARCHERY_TALLIES, tallies) };
		const v = value(i);
		const row: SaveResultsBody["results"][number] = {
			meet_competitor_id: slot.meet_competitor_id,
			version: slot.version,
			result_value: v,
			result_display: clip(so !== null ? `${v} (S-O ${so})` : String(v)),
			detail,
		};
		if (headToHead) {
			const outcome = outcomes ? outcomes[i] : null;
			row.outcome = outcome;
			row.rank = outcome === "WIN" ? 1 : outcome === "LOSS" ? 2 : null;
		}
		return row;
	});

	const so = shootOff ? ` (S-O ${soTotal(0)}${DASH}${soTotal(1)})` : "";
	return { body: { results: results as SaveResultsBody["results"] }, warnings, summary: `${value(0)}${DASH}${value(1)}${so}` };
}

// ─── Shooting ────────────────────────────────────────────────────────────────

export interface TsrShootingSaveInput {
	/** The stored feed envelope (`shooting_state:<id>`, as `join_shooting` / `/state` return it). */
	envelope: Pick<TsrShootingFeedEnvelope, "documents">;
	/** Every FILLED slot of the meet as loaded (`GET /meets/:id`). */
	slots: readonly TsrSaveSlot[];
}

const num = (s: string | null | undefined): number | null => {
	if (s === null || s === undefined) return null;
	const t = String(s).trim().replace(",", ".");
	if (!/^-?\d+(\.\d+)?$/.test(t)) return null;
	const n = Number(t);
	return Number.isFinite(n) ? n : null;
};

/** `series_1..n`, else `round_1..n`, in numeric order. */
function seriesRounds(scores: Record<string, string>): Round[] {
	const pick = (prefix: string) =>
		Object.keys(scores)
			.map((k) => ({ k, m: new RegExp(`^${prefix}_(\\d+)$`).exec(k) }))
			.filter((x): x is { k: string; m: RegExpExecArray } => !!x.m)
			.map((x) => ({ seq: Number(x.m[1]), value: num(scores[x.k]) }))
			.filter((r): r is { seq: number; value: number } => r.seq >= 1 && r.value !== null)
			.sort((a, b) => a.seq - b.seq);
	const series = pick("series");
	return (series.length > 0 ? series : pick("round")).slice(0, RESULT_FAMILY_ARRAY_MAX);
}

const IRM_SET: ReadonlySet<string> = new Set(IRM_CODES);

/** Every addressed row's scores, documents applied in order (later wins per key) — v1's projection rule. */
export function tsrShootingRowsBySlot(envelope: Pick<TsrShootingFeedEnvelope, "documents">): Map<string, { row: TsrShootingRow; scores: Record<string, string> }> {
	const out = new Map<string, { row: TsrShootingRow; scores: Record<string, string> }>();
	for (const doc of envelope.documents ?? []) {
		for (const row of doc.rows ?? []) {
			const id = row.meetParticipantId;
			if (!id) continue;
			const prev = out.get(id);
			out.set(id, { row: { ...(prev?.row ?? {}), ...row } as TsrShootingRow, scores: { ...(prev?.scores ?? {}), ...(row.scores ?? {}) } });
		}
	}
	return out;
}

export function tsrShootingSaveBody(input: TsrShootingSaveInput): TsrRoundsSaveResult {
	const bySlot = new Map(input.slots.map((s) => [s.meet_competitor_id, s]));
	const warnings: TsrRoundsWarning[] = [];
	const results: SaveResultsBody["results"] = [];
	for (const [id, { row, scores }] of tsrShootingRowsBySlot(input.envelope)) {
		const slot = bySlot.get(id);
		if (!slot) {
			warnings.push({ code: "FOREIGN_SLOT", meet_competitor_id: id });
			continue;
		}
		const rounds = seriesRounds(scores);
		const printed = scores.final_score ?? scores.qualification_total ?? row.result ?? null;
		const headline = num(printed) ?? (rounds.length > 0 ? Math.round(rounds.reduce((t, r) => t + r.value, 0) * 10) / 10 : null);
		const irmRaw = row.irm?.trim().toUpperCase() ?? "";
		if (headline === null && rounds.length === 0 && !irmRaw) continue; // nothing shot yet
		const inner = num(scores.inner_tens);
		const tallies: Tally[] = [
			...(inner !== null ? [{ code: "inner_tens", value: inner }] : []),
			...(typeof row.shotsFired === "number" ? [{ code: "shots_fired", value: row.shotsFired }] : []),
		];
		const current = baseDetail(slot);
		const out: SaveResultsBody["results"][number] = {
			meet_competitor_id: id,
			version: slot.version,
			result_value: headline,
			result_display:
				headline === null ? null : clip(inner !== null && scores.final_score === undefined ? `${printed ?? headline}-${inner}x` : String(printed ?? headline)),
			detail: { ...current, rounds, tallies: mergeTallies(current, OWN_SHOOTING_TALLIES, tallies) },
		};
		const rank = row.rank ?? num(scores.rank);
		if (rank !== null && Number.isInteger(rank) && rank >= 1 && rank <= RESULT_RANK_MAX) {
			out.rank = rank;
			// The range ranks the field (a final's elimination order is not the total): OVR's live ranking keeps it.
			out.detail = { ...(out.detail as RoundsResultDetail), rank_locked: true };
		}
		if (irmRaw) {
			if (IRM_SET.has(irmRaw)) out.irm = irmRaw as IrmCode;
			else warnings.push({ code: "UNKNOWN_IRM", meet_competitor_id: id, irm: irmRaw });
		}
		results.push(out);
	}
	if (results.length === 0) throw new TsrRoundsMappingError("NOTHING_TO_SAVE", "No row of the feed is addressed to a slot of this meet");
	return { body: { results }, warnings, summary: String(results.length) };
}
